import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  Menu, 
  X, 
  Home, 
  LogOut, 
  User, 
  Settings, 
  Shield, 
  ChevronDown,
  ChevronUp,
  FileText,
  Users,
  CreditCard,
  Coins,
  Share2,
  Trophy
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import CreditStatus from '../billing/CreditStatus';
import logo from '../../assets/logo.png';
import '../../fonts.css';

// List of admin UIDs - you should store these in your Firestore database in a separate collection
const ADMIN_UIDS = [
  // Add your admin UIDs here
  "iMTsoOPrg6eR4rlJmaL1f0pA07Y2"
];

const SidebarLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const isAdmin = currentUser && ADMIN_UIDS.includes(currentUser.uid);
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };
  
  const closeSidebar = () => {
    setSidebarOpen(false);
  };
  
  // Only render the sidebar if user is logged in
  if (!currentUser) {
    return children;
  }
  
  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
          onClick={closeSidebar}
        />
      )}
      
      {/* Sidebar - more direct height and overflow approach */}
      <div 
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-lg transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:static lg:inset-0 transition duration-300 ease-in-out h-full`}
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        {/* Logo/header section - this stays fixed */}
        <div className="h-16 px-6 border-b" style={{ flexShrink: 0 }}>
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center justify-center h-full w-full">
              <img 
                src={logo}
                alt="Flokraft Logo" 
                className="h-12 w-auto mx-auto"
              />
            </div>
            <button 
              className="p-2 rounded-md lg:hidden"
              onClick={closeSidebar}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Navigation section - this is scrollable - using inline styles for reliability */}
        <div className="p-4" style={{ overflowY: 'auto', height: 'calc(100% - 64px)' }}>
          {/* User Info */}
          <div className="mb-6 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center mb-2">
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center mr-3">
                <User className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {currentUser.displayName || currentUser.email}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {currentUser.email}
                </p>
              </div>
            </div>
            
            {/* Credits Status Component */}
            <CreditStatus />
          </div>
          
          {/* Navigation Items */}
          <ul className="space-y-1.5">
            <li>
              <Link 
                to="/" 
                className={`flex items-center px-3 py-2 rounded-md ${
                  location.pathname === '/' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={closeSidebar}
              >
                <Home className="w-5 h-5 mr-3" />
                Dashboard
              </Link>
            </li>
            
            <li>
              <Link 
                to="/credits" 
                className={`flex items-center px-3 py-2 rounded-md ${
                  location.pathname === '/credits' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={closeSidebar}
              >
                <Coins className="w-5 h-5 mr-3" />
                Buy Credits
              </Link>
            </li>
            
            {/* Only show Admin link for admins */}
            {isAdmin && (
              <li>
                <Link 
                  to="/admin" 
                  className={`flex items-center px-3 py-2 rounded-md ${
                    location.pathname.startsWith('/admin') 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={closeSidebar}
                >
                  <Shield className="w-5 h-5 mr-3" />
                  Admin Panel
                </Link>
              </li>
            )}

            <li>
              <Link 
                to="/shared-analyses" 
                className={`flex items-center px-3 py-2 rounded-md ${
                  location.pathname === '/shared-analyses' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={closeSidebar}
              >
                <Share2 className="w-5 h-5 mr-3" />
                Shared With Me
              </Link>
            </li>

            <li>
              <Link 
                to="/events" 
                className={`flex items-center px-3 py-2 rounded-md ${
                  location.pathname.startsWith('/events') 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={closeSidebar}
              >
                <Trophy className="w-5 h-5 mr-3" />
                Events
              </Link>
            </li>
            
            <li>
              <Link 
                to="/profile" 
                className={`flex items-center px-3 py-2 rounded-md ${
                  location.pathname === '/profile' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={closeSidebar}
              >
                <Settings className="w-5 h-5 mr-3" />
                Profile Settings
              </Link>
            </li>
            
            <li>
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Logout
              </button>
            </li>
          </ul>
          
          {/* Analysis Type Toggle */}
          {/* <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Analysis Type
            </h3>
            <div className="mt-2 space-y-1.5">
              <Link 
                to="/?type=couple" 
                className={`flex items-center px-3 py-2 rounded-md ${
                  (!location.search.includes('type=') || location.search.includes('type=couple')) 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={closeSidebar}
              >
                <FileText className="w-5 h-5 mr-3" />
                Couple Analysis
              </Link>
              <Link 
                to="/?type=solo" 
                className={`flex items-center px-3 py-2 rounded-md ${
                  location.search.includes('type=solo') 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={closeSidebar}
              >
                <User className="w-5 h-5 mr-3" />
                Solo Analysis
              </Link>
              <Link 
                to="/?type=duo" 
                className={`flex items-center px-3 py-2 rounded-md ${
                  location.search.includes('type=duo') 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={closeSidebar}
              >
                <Users className="w-5 h-5 mr-3" />
                Duo Analysis
              </Link>
     
              <Link 
                to="/?type=formation" 
                className={`flex items-center px-3 py-2 rounded-md ${
                  location.search.includes('type=formation') 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={closeSidebar}
              >
                <Users className="w-5 h-5 mr-3" />
                Formation Analysis
              </Link>
            </div>
          </div> */}
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top header */}
        <header className="bg-white shadow-sm h-16 flex items-center justify-between px-4 lg:px-6">
          <button 
            className="p-2 rounded-md lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </button>
          
          <div className="metalord-font">
            AI Match Analysis
          </div>
          
          <div className="flex items-center">
            {/* Mobile credits status */}
            <div className="lg:hidden">
              <Link to="/credits" className="flex items-center p-2 rounded-md text-blue-600">
                <Coins className="w-5 h-5 mr-1" />
                <span className="font-semibold">Credits</span>
              </Link>
            </div>
          </div>
        </header>
        
        {/* Main content area */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default SidebarLayout;